import * as React from 'react'

import { Box, Form, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessTemplateVisibilitySettingsFragment,
  UpdatePerformanceProcessTemplateVisibilitySettingsDocument,
  UpdatePerformanceProcessTemplateVisibilitySettingsMutationVariables,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import {
  SettingsVisibility,
  type TypeSettingsVisibility,
} from 'modules/performance_process/components/SettingsVisibility'

export const CustomTemplateSettingsVisibility: React.FC<{
  template: PerformanceProcessTemplateVisibilitySettingsFragment
}> = ({ template }) => {
  const [updateVisibilitySetting, { loading }] = useMutation(
    UpdatePerformanceProcessTemplateVisibilitySettingsDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.updateVisibilitySettingsPerformanceReviewProcessCustomTemplate,
    }
  )

  const form = useForm<UpdatePerformanceProcessTemplateVisibilitySettingsMutationVariables>({
    defaultValues: {
      id: Number(template.id),
      earlyAccessToAnswersForManagers:
        template.visibilitySettings?.earlyAccessToAnswersForManagers ?? true,
      restrictAnswersVisibilityToReportees:
        template.visibilitySettings?.restrictAnswersVisibilityToReportees ?? false,
      anonymousPeerEvaluationForTarget:
        template.visibilitySettings?.anonymousPeerEvaluationForTarget ?? false,
    },
    validate: (data) => {
      const errors: FormErrors<UpdatePerformanceProcessTemplateVisibilitySettingsMutationVariables> =
        {}
      if (!data.id) {
        errors.id = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: (data) => {
      return updateVisibilitySetting({
        variables: {
          id: data.id,
          earlyAccessToAnswersForManagers: data.earlyAccessToAnswersForManagers,
          restrictAnswersVisibilityToReportees: data.restrictAnswersVisibilityToReportees,
          anonymousPeerEvaluationForTarget: data.anonymousPeerEvaluationForTarget,
        },
      }).then((response) => handleResult(response, () => {}))
    },
  })

  const onChange = (field: keyof TypeSettingsVisibility, enabled: boolean) => {
    form.setValues({
      ...form.values,
      [field]: enabled,
    })
    form.handleSubmit()
  }

  const disabled = form.isSubmitting || loading || !form.isValid

  return (
    <Box gap="s32">
      <Box gap="s16">
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('performance_process.reviews.visibility')}
        </Text>
        <Text size="300">{i18n.t('performance_process.reviews.templates.edit.description')}</Text>
      </Box>
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <SettingsVisibility
          disabled={disabled}
          settings={{
            earlyAccessToAnswersForManagers: form.values.earlyAccessToAnswersForManagers,
            restrictAnswersVisibilityToReportees: form.values.restrictAnswersVisibilityToReportees,
            anonymousPeerEvaluationForTarget: form.values.anonymousPeerEvaluationForTarget,
          }}
          onSettingToggled={onChange}
        />
      </Form>
    </Box>
  )
}
